import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import AgentPicker from './AgentPicker';
import {
  Phone, Headphones, ClipboardList, Presentation, DollarSign, Users, Loader2, Grid3x3, Clock,
} from 'lucide-react';
import {
  format, startOfWeek, endOfWeek, startOfMonth, addDays, differenceInDays,
  subWeeks, isSameDay,
} from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iso = (d) => format(d, 'yyyy-MM-dd');

// Row config — each metric has its own hue used to shade the heatmap cells.
// [h, s%] pairs; lightness varies with intensity.
const ROWS = [
  { key: 'dials_made',           label: 'Dials',         icon: Phone,         hsl: [230, 65] }, // deep blue
  { key: 'contacts_made',        label: 'Contacts',      icon: Headphones,    hsl: [38,  92] }, // amber
  { key: 'appointments_set',     label: 'Appointments',  icon: ClipboardList, hsl: [24,  95] }, // orange
  { key: 'presentations_given',  label: 'Presentations', icon: Presentation,  hsl: [340, 82] }, // pink/red
  { key: 'sales_made',           label: 'Sales',         icon: DollarSign,    hsl: [155, 65] }, // green
  { key: 'recruits_onboarded',   label: 'Recruits',      icon: Users,         hsl: [265, 70] }, // violet
  { key: 'hours_worked',         label: 'Hours Worked',  icon: Clock,         hsl: [200, 75], derived: true }, // cyan — auto-calculated
];

// Auto-calculated work time (minutes): 1 dial = 1 min, 1 presentation = 60 min, 1 recruit = 30 min.
const entryVal = (e, r) => {
  if (!e) return 0;
  if (r.derived) {
    return (e.dials_made || 0) * 1 + (e.presentations_given || 0) * 60 + (e.recruits_onboarded || 0) * 30;
  }
  return e[r.key] || 0;
};

// Compact duration: 0 → '0', <60 → '45m', else '2h' / '2.6h'.
const fmtDuration = (mins) => {
  if (!mins) return '0';
  if (mins < 60) return `${mins}m`;
  const r = Math.round((mins / 60) * 10) / 10;
  return `${Number.isInteger(r) ? r : r.toFixed(1)}h`;
};

const RANGE_OPTIONS = [
  { k: 'thisweek', label: 'This Week' },
  { k: 'lastweek', label: 'Last Week' },
  { k: 'mtd',      label: 'Month to Date' },
];

// Build the list of day-Date objects to render for a given range.
const buildDays = (rangeKey) => {
  const today = new Date();
  if (rangeKey === 'thisweek' || rangeKey === 'lastweek') {
    const anchor = rangeKey === 'lastweek' ? subWeeks(today, 1) : today;
    const start = startOfWeek(anchor, { weekStartsOn: 0 });
    const end   = endOfWeek(anchor,   { weekStartsOn: 0 });
    const n = differenceInDays(end, start) + 1;
    return Array.from({ length: n }, (_, i) => addDays(start, i));
  }
  // mtd
  const start = startOfMonth(today);
  const n = differenceInDays(today, start) + 1;
  return Array.from({ length: n }, (_, i) => addDays(start, i));
};

// Shade a cell: value 0 → very light; value == max → deep saturated.
const cellShade = (value, max, hsl) => {
  if (!value || value <= 0) return { bg: '#F7F9FC', text: '#C9D2E8', ring: 'transparent' };
  const [h, s] = hsl;
  const t = Math.min(1, value / Math.max(1, max));       // 0..1
  const lightness = 68 - Math.round(t * 34);              // 68% → 34%
  const bg = `hsl(${h}, ${s}%, ${lightness}%)`;
  const text = lightness < 55 ? '#FFFFFF' : '#1F2937';
  const ring = `hsl(${h}, ${s}%, ${Math.max(20, lightness - 12)}%)`;
  return { bg, text, ring };
};

export default function TrackerHeatmap() {
  const { user, getAuthHeader } = useAuth();
  const [rangeKey, setRangeKey] = useState('thisweek');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  const days = useMemo(() => buildDays(rangeKey), [rangeKey]);

  const startISO = useMemo(() => (days.length ? iso(days[0])              : null), [days]);
  const endISO   = useMemo(() => (days.length ? iso(days[days.length - 1]) : null), [days]);

  // Load viewable agents (self + downline; admin = everyone)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/tracker/viewable-agents`, getAuthHeader());
        if (!cancelled && Array.isArray(res.data)) setAgents(res.data);
      } catch (e) {
        // silent — picker just won't render
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedAgent = useMemo(() => {
    if (selectedAgentId) return agents.find((a) => a.id === selectedAgentId) || null;
    return agents.find((a) => a.is_self) || null;
  }, [agents, selectedAgentId]);

  const viewingSelf = !selectedAgentId || selectedAgentId === user?.id;

  const fetchEntries = useCallback(async () => {
    if (!startISO || !endISO) return;
    try {
      setLoading(true);
      const params = { start_date: startISO, end_date: endISO };
      if (selectedAgentId && selectedAgentId !== user?.id) params.user_id = selectedAgentId;
      const res = await axios.get(`${API}/kpi`, { ...getAuthHeader(), params });
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [startISO, endISO, selectedAgentId, user?.id, getAuthHeader]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Map { 'yyyy-MM-dd': entry }
  const byDay = useMemo(() => {
    const m = {};
    for (const e of entries) m[e.date] = e;
    return m;
  }, [entries]);

  // Per-row max for shading scale
  const maxByRow = useMemo(() => {
    const out = {};
    for (const r of ROWS) {
      let max = 0;
      for (const d of days) {
        const v = entryVal(byDay[iso(d)], r);
        if (v > max) max = v;
      }
      out[r.key] = max;
    }
    return out;
  }, [byDay, days]);

  const totalByRow = useMemo(() => {
    const out = {};
    for (const r of ROWS) {
      let total = 0;
      for (const d of days) total += entryVal(byDay[iso(d)], r);
      out[r.key] = total;
    }
    return out;
  }, [byDay, days]);

  const today = new Date();
  const rangeLabel = useMemo(() => {
    if (rangeKey === 'thisweek') return `Week of ${format(days[0] || today, 'MMM d')}`;
    if (rangeKey === 'lastweek') return `Week of ${format(days[0] || today, 'MMM d')}`;
    return `${format(today, 'MMMM yyyy')} — through today`;
  }, [rangeKey, days, today]);

  return (
    <Card className="bg-white" data-testid="tracker-heatmap">
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#A3AED0]">
              <Grid3x3 className="h-4 w-4 text-[#4318FF]" /> Activity Grid
              {!viewingSelf && selectedAgent && (
                <span className="normal-case tracking-normal rounded-full bg-[#EEF1FF] px-2 py-0.5 text-[10px] font-bold text-[#4318FF]" data-testid="heatmap-viewing-badge">
                  Viewing {selectedAgent.name}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-[11px] text-[#A3AED0]">
              {rangeLabel} — cells shade darker as the number grows.
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <AgentPicker
              agents={agents}
              selectedAgentId={selectedAgentId}
              onChange={setSelectedAgentId}
              testIdPrefix="heatmap"
            />
            <Select value={rangeKey} onValueChange={setRangeKey}>
              <SelectTrigger className="h-9 w-[160px] text-xs" data-testid="heatmap-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((o) => (
                  <SelectItem key={o.k} value={o.k}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#2447D6]" /></div>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0" data-testid="heatmap-scroll">
            <table className="border-separate border-spacing-y-1 border-spacing-x-1 min-w-full">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-white w-[150px] min-w-[150px] pr-2" />
                  {days.map((d) => {
                    const isToday = isSameDay(d, today);
                    return (
                      <th key={iso(d)} className="min-w-[46px] px-0.5 text-center">
                        <div className={`text-[10px] font-semibold uppercase tracking-wide ${isToday ? 'text-[#4318FF]' : 'text-[#A3AED0]'}`}>
                          {format(d, 'EEE')}
                        </div>
                        <div className={`text-[11px] tabular-nums ${isToday ? 'text-[#4318FF] font-bold' : 'text-[#A3AED0]'}`}>
                          {format(d, 'd')}
                        </div>
                      </th>
                    );
                  })}
                  <th className="min-w-[64px] pl-2 text-right">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-[#A3AED0]">Total</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => {
                  const max = maxByRow[r.key] || 0;
                  return (
                    <tr key={r.key} data-testid={`heatmap-row-${r.key}`}>
                      <td className="sticky left-0 z-10 bg-white pr-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="flex h-6 w-6 items-center justify-center rounded-md"
                            style={{ backgroundColor: `hsl(${r.hsl[0]}, ${r.hsl[1]}%, 94%)` }}
                          >
                            <r.icon className="h-3.5 w-3.5" style={{ color: `hsl(${r.hsl[0]}, ${r.hsl[1]}%, 40%)` }} />
                          </span>
                          <span className="text-[13px] font-semibold text-[#2B3674]">{r.label}</span>
                          {r.derived && (
                            <span className="rounded bg-[#F4F7FE] px-1 py-0.5 text-[8px] font-bold uppercase tracking-wide text-[#A3AED0]" title="Auto-calculated: 1 dial = 1 min, 1 presentation = 1 hr, 1 recruit = 30 min">
                              Auto
                            </span>
                          )}
                        </div>
                      </td>
                      {days.map((d) => {
                        const key = iso(d);
                        const v = entryVal(byDay[key], r);
                        const s = cellShade(v, max, r.hsl);
                        const isToday = isSameDay(d, today);
                        return (
                          <td key={key} className="p-0">
                            <div
                              data-testid={`heatmap-cell-${r.key}-${key}`}
                              className={`mx-auto flex h-9 min-w-[42px] items-center justify-center rounded-md font-['Manrope'] font-bold tabular-nums transition ${r.derived ? 'text-xs' : 'text-sm'}`}
                              style={{
                                backgroundColor: s.bg,
                                color: s.text,
                                boxShadow: isToday ? `0 0 0 2px ${s.ring}` : 'none',
                              }}
                              title={r.derived
                                ? `${format(d, 'EEE, MMM d')} — ${fmtDuration(v)} worked (auto-calculated)`
                                : `${format(d, 'EEE, MMM d')} — ${v} ${r.label.toLowerCase()}`}
                            >
                              {r.derived ? fmtDuration(v) : v}
                            </div>
                          </td>
                        );
                      })}
                      <td className="pl-2 text-right">
                        <div className="font-['Manrope'] text-sm font-extrabold text-[#2B3674] tabular-nums">
                          {r.derived ? fmtDuration(totalByRow[r.key]) : (totalByRow[r.key] || 0)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
